import React, { useState, useEffect } from 'react';
import { FiHash, FiCopy, FiCheck, FiChevronRight, FiSearch, FiClock, FiLayers, FiEyeOff, FiPlayCircle } from 'react-icons/fi';
import { BsTerminal, BsTable, BsDiagram3 } from 'react-icons/bs';

const SECTIONS = [
  { id: 'basics', title: 'Basic Queries' },
  { id: 'filtering', title: 'Filtering Data' },
  { id: 'ordering', title: 'Sorting & Limiting' },
  { id: 'conditionals', title: 'Conditional Logic' },
  { id: 'joins', title: 'Joins & Relationships' },
  { id: 'sets', title: 'Set Operations' },
  { id: 'aggregation', title: 'Aggregation & Grouping' },
  { id: 'window', title: 'Window Functions' },
  { id: 'ctes', title: 'Subqueries & CTEs' },
  { id: 'dates', title: 'Date & Time' },
  { id: 'conversion', title: 'Type Conversion' }
];

const SQLCheatsheet = () => {
  const [activeSection, setActiveSection] = useState('basics');
  const [copiedId, setCopiedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  const filteredSections = SECTIONS.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    SECTIONS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const ExampleTable = ({ data, title, highlight }) => {
    if (!data || data.length === 0) return null;
    const columns = Object.keys(data[0]);

    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-[#0f172a] text-xs font-mono mb-4 shadow-sm dark:shadow-none">
        {title && <div className="bg-gray-100 dark:bg-[#1e293b] px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-400 font-semibold">{title}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 dark:bg-[#1e293b]/50 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400">
                {columns.map(col => <th key={col} className="px-3 py-2 font-medium">{col}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.map((row, i) => (
                <tr key={i} className={highlight && highlight(row) ? "bg-green-500/10" : ""}>
                  {columns.map(col => (
                    <td key={col} className={`px-3 py-2 ${highlight && highlight(row) ? "text-green-600 dark:text-green-300" : "text-gray-600 dark:text-gray-300"}`}>
                      {row[col]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const CodeBlock = ({ code, id, label, example }) => {
    const [showExample, setShowExample] = useState(false);

    return (
      <div className="relative group my-6 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] shadow-lg transition-all hover:border-gray-300 dark:hover:border-gray-500">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-[#1e293b] border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs font-mono text-blue-400 flex items-center gap-2">
            <BsTerminal size={12} /> {label || 'SQL'}
          </span>
          <div className="flex items-center gap-2">
            {example && (
              <button
                onClick={() => setShowExample(!showExample)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors border ${showExample ? 'bg-blue-500/10 border-blue-500/50 text-blue-600 dark:text-blue-400' : 'bg-transparent border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                {showExample ? <FiEyeOff size={12} /> : <FiPlayCircle size={12} />}
                {showExample ? 'Hide Result' : 'Run Example'}
              </button>
            )}
            <button
              onClick={() => handleCopy(code, id)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-1"
              title="Copy to clipboard"
            >
              {copiedId === id ? <FiCheck className="text-green-400" /> : <FiCopy size={14} />}
            </button>
          </div>
        </div>

        <div className="p-4 overflow-x-auto bg-gray-50 dark:bg-[#0b1221]">
          <pre className="text-sm font-mono text-gray-800 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
            <code dangerouslySetInnerHTML={{
              __html: code.replace(/\b(SELECT|FROM|WHERE|AND|OR|NOT|IN|BETWEEN|LIKE|ORDER BY|ASC|DESC|LIMIT|JOIN|ON|GROUP BY|HAVING|COUNT|SUM|AVG|MIN|MAX|AS|DISTINCT|NULL|IS|INNER|LEFT|RIGHT|FULL|OUTER|UNION|ALL|CASE|WHEN|THEN|ELSE|END|OVER|PARTITION|BY|ROW_NUMBER|RANK|DENSE_RANK|LEAD|LAG|DATE_TRUNC|EXTRACT|NOW|CURRENT_DATE|INTERVAL|WITH)\b/g,
                '<span class="text-blue-600 dark:text-purple-400 font-bold">$1</span>')
                .replace(/'([^']*)'/g, '<span class="text-green-600 dark:text-green-400">\'$1\'</span>')
                .replace(/--.*$/gm, '<span class="text-gray-500 italic">$&</span>')
            }} />
          </pre>
        </div>

        {showExample && example && (
          <div className="bg-white dark:bg-[#162032] border-t border-gray-200 dark:border-gray-700 p-4 animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2 ml-1">Input Table(s)</span>
                <div className="space-y-4">
                  {example.input.map((table, idx) => (
                    <ExampleTable key={idx} data={table.data} title={table.name} highlight={table.highlight} />
                  ))}
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-2 ml-1">
                  <span className="text-xs uppercase tracking-wider text-teal-500 font-bold">Query Result</span>
                  <span className="text-[10px] bg-teal-500/10 text-teal-400 px-1.5 py-0.5 rounded border border-teal-500/20">Success</span>
                </div>
                <ExampleTable data={example.output} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- MOCK DATA FOR EXAMPLES ---
  const MOCK_USERS = [
    { id: 1, name: 'Alice', country: 'USA' },
    { id: 2, name: 'Bob', country: 'Canada' },
    { id: 3, name: 'Charlie', country: 'USA' },
  ];

  const MOCK_EMPLOYEES = [
    { id: 1, name: 'Alice', salary: 120000 },
    { id: 2, name: 'Bob', salary: 80000 },
    { id: 3, name: 'Charlie', salary: 95000 }
  ];

  const MOCK_ORDERS = [
    { id: 101, user_id: 1, amount: 50, date: '2023-01-01' },
    { id: 102, user_id: 2, amount: 20, date: '2023-01-05' },
    { id: 103, user_id: 1, amount: 100, date: '2023-01-10' },
  ];

  const MOCK_SALES = [
    { region: 'North', date: '2023-01', amount: 100 },
    { region: 'North', date: '2023-02', amount: 150 },
    { region: 'North', date: '2023-02', amount: 150 },
    { region: 'South', date: '2023-01', amount: 200 },
  ];

  const MOCK_INVENTORY = [
    { item: 'Widget A', stock: 50 },
    { item: 'Widget B', stock: null },
    { item: 'Widget C', stock: 0 },
  ];



  return (
    <div className="min-h-screen bg-white dark:bg-[#0a1220] text-gray-900 dark:text-gray-200 font-sans selection:bg-blue-500/30 transition-colors duration-300">

      <div className="bg-gray-50 border-b border-gray-200 dark:bg-gradient-to-r dark:from-[#1e3a5f] dark:to-[#0a1220] dark:border-gray-800 pt-8 pb-12 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 dark:from-amber-300 dark:via-orange-500 dark:to-red-500 text-transparent bg-clip-text inline-block">
            SQL Cheatsheet
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl">
            The ultimate quick reference guide for Data Scientists and Analysts.
            Master the syntax for querying, filtering, and analyzing data.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row gap-12 relative">
        <aside className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24 bg-gray-50 dark:bg-[#162032]/50 backdrop-blur-sm p-4 rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="relative mb-4">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white dark:bg-[#0b1221] border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none transition-all placeholder:text-gray-400"
              />
            </div>
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">Contents</h3>
            <nav className="space-y-1 max-h-[calc(100vh-250px)] overflow-y-auto custom-scrollbar">
              {filteredSections.map(({ id, title }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between group ${activeSection === id
                    ? 'bg-orange-50 dark:bg-[#f97316]/10 text-orange-600 dark:text-[#f97316] font-medium border-l-2 border-orange-500 dark:border-[#f97316]'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1e293b] hover:text-gray-900 dark:hover:text-white border-l-2 border-transparent'
                    }`}
                >
                  {title}
                  {activeSection === id && <FiChevronRight className="text-orange-500 dark:text-[#f97316]" />}
                </button>
              ))}
              {filteredSections.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center italic">
                  No topics found
                </div>
              )}
            </nav>
          </div>
        </aside>

        <main className="flex-1 min-w-0 space-y-16 pb-20">

          {/* BASICS */}
          <section id="basics" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg text-blue-600 dark:text-blue-400">
                <BsTable size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Basic Queries</h2>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                  <FiHash className="text-gray-500" /> Retrieving Data
                </h3>
                <CodeBlock
                  label="Select All Columns"
                  id="select-all"
                  code={`-- Select all rows and columns from the 'users' table\nSELECT * \nFROM users;`}
                  example={{
                    input: [{ name: 'users', data: MOCK_USERS }],
                    output: MOCK_USERS
                  }}
                />
                <CodeBlock
                  label="Select Specific Columns"
                  id="select-specific"
                  code={`-- Select only 'name' and 'country' columns\nSELECT name, country \nFROM users;`}
                  example={{
                    input: [{ name: 'users', data: MOCK_USERS }],
                    output: MOCK_USERS.map(({ name, country }) => ({ name, country }))
                  }}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                  <FiHash className="text-gray-500" /> Unique Values
                </h3>
                <CodeBlock
                  label="Distinct"
                  id="distinct"
                  code={`-- Get a list of unique countries\nSELECT DISTINCT country \nFROM users;`}
                  example={{
                    input: [{ name: 'users', data: MOCK_USERS }],
                    output: [{ country: 'USA' }, { country: 'Canada' }]
                  }}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                  <FiHash className="text-gray-500" /> Aliasing
                </h3>
                <CodeBlock
                  label="Aliases"
                  id="aliases"
                  code={`-- Rename columns for better readability\nSELECT name AS Employee, salary / 12 AS Monthly_Pay \nFROM employees;`}
                  example={{
                    input: [{ name: 'employees', data: MOCK_EMPLOYEES }],
                    output: MOCK_EMPLOYEES.map(e => ({ Employee: e.name, Monthly_Pay: Math.floor(e.salary / 12) }))
                  }}
                />
              </div>
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* FILTERING */}
          <section id="filtering" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 dark:bg-purple-500/20 rounded-lg text-purple-600 dark:text-purple-400">
                <FiSearch size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Filtering Data</h2>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Comparison Operators</h3>
                <CodeBlock
                  label="Basic Comparison"
                  id="comparison"
                  code={`-- Equality and Inequality\nSELECT * FROM users \nWHERE age >= 18 AND country != 'France';`}
                  example={null}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Logical & Range</h3>
                <CodeBlock
                  label="Logical Operators"
                  id="logical"
                  code={`-- Combining conditions\nSELECT * FROM products \nWHERE (category = 'Electronics' OR category = 'Books') \nAND NOT status = 'Out of Stock';`}
                  example={null}
                />
                <CodeBlock
                  label="Between"
                  id="between"
                  code={`-- Value within range (inclusive)\nSELECT * FROM orders \nWHERE amount BETWEEN 10 AND 100;`}
                  example={{
                    input: [{ name: 'orders', data: MOCK_ORDERS }],
                    output: MOCK_ORDERS.filter(o => o.amount >= 10 && o.amount <= 100)
                  }}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Pattern & Nulls</h3>
                <CodeBlock
                  label="IN List"
                  id="in-operator"
                  code={`-- Set of values\nSELECT * FROM users \nWHERE country IN ('USA', 'Canada', 'UK');`}
                  example={null}
                />
                <CodeBlock
                  label="LIKE Pattern"
                  id="like"
                  code={`-- 'A%' matches anything starting with A\nSELECT * FROM users \nWHERE name LIKE 'A%';`}
                  example={{
                    input: [{ name: 'users', data: MOCK_USERS, highlight: (row) => row.name.startsWith('A') }],
                    output: MOCK_USERS.filter(u => u.name.startsWith('A'))
                  }}
                />
                <CodeBlock
                  label="IS NULL"
                  id="is-null"
                  code={`-- Check for missing values\nSELECT * FROM users \nWHERE phone IS NULL;`}
                  example={null}
                />
              </div>
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* ORDERING */}
          <section id="ordering" className="scroll-mt-32">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Sorting & Limiting</h2>
            <div className="space-y-8">
              <CodeBlock
                label="Order By"
                id="orderby"
                code={`-- Sort users by name alphabetically (Ascending)\nSELECT * \nFROM users \nORDER BY name ASC;`}
                example={{
                  input: [{ name: 'users', data: MOCK_USERS }],
                  output: [...MOCK_USERS].sort((a, b) => a.name.localeCompare(b.name))
                }}
              />
              <CodeBlock
                label="Limit Results"
                id="limit"
                code={`-- Get the top 2 highest paid employees\nSELECT * \nFROM employees \nORDER BY salary DESC \nLIMIT 2;`}
                example={{
                  input: [{ name: 'employees', data: MOCK_EMPLOYEES }],
                  output: [...MOCK_EMPLOYEES].sort((a, b) => b.salary - a.salary).slice(0, 2)
                }}
              />
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* CONDITIONALS */}
          <section id="conditionals" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-pink-100 dark:bg-pink-500/20 rounded-lg text-pink-600 dark:text-pink-400">
                <BsDiagram3 size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Conditional Logic</h2>
            </div>
            <div className="space-y-8">
              <CodeBlock
                label="Case When"
                id="case-when"
                code={`-- Conditional categorization\nSELECT name, \n  CASE \n    WHEN salary > 100000 THEN 'High Earner' \n    WHEN salary > 80000 THEN 'Mid Earner'\n    ELSE 'Standard' \n  END as salary_tier \nFROM employees;`}
                example={{
                  input: [{ name: 'employees', data: MOCK_EMPLOYEES }],
                  output: [
                    { name: 'Alice', salary_tier: 'High Earner' },
                    { name: 'Bob', salary_tier: 'Standard' },
                    { name: 'Charlie', salary_tier: 'Mid Earner' }
                  ]
                }}
              />
              <CodeBlock
                label="IF Function"
                id="if-func"
                code={`-- Simple if/else logic\nSELECT name, IF(salary > 90000, 'Yes', 'No') as is_eligible \nFROM employees;`}
                example={{
                  input: [{ name: 'employees', data: MOCK_EMPLOYEES }],
                  output: [
                    { name: 'Alice', is_eligible: 'Yes' },
                    { name: 'Bob', is_eligible: 'No' },
                    { name: 'Charlie', is_eligible: 'Yes' }
                  ]
                }}
              />
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* JOINS */}
          <section id="joins" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 dark:bg-green-500/20 rounded-lg text-green-600 dark:text-green-400">
                <BsDiagram3 size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Joins & Relationships</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* INNER JOIN */}
              <div className="bg-white dark:bg-[#162032] p-6 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col items-center hover:border-green-500/30 transition-colors shadow-sm dark:shadow-none">
                <h4 className="font-bold text-gray-900 dark:text-white mb-4 text-xs tracking-widest">INNER JOIN</h4>
                <svg width="120" height="80" viewBox="0 0 120 80" className="opacity-90">
                  <circle cx="45" cy="40" r="30" fill="none" stroke="#64748b" strokeWidth="2" />
                  <circle cx="75" cy="40" r="30" fill="none" stroke="#64748b" strokeWidth="2" />
                  {/* Intersection path approximately */}
                  <path d="M 75 40 A 30 30 0 0 0 60 14 A 30 30 0 0 0 60 66 A 30 30 0 0 0 75 40 Z" fill="#22c55e" stroke="#22c55e" strokeWidth="2" />
                  {/* The path logic above is partial, using a clipping or simpler approach is better for robustness */}
                  <defs>
                    <clipPath id="circleLeft">
                      <circle cx="45" cy="40" r="30" />
                    </clipPath>
                  </defs>
                  <circle cx="75" cy="40" r="30" fill="#22c55e" clipPath="url(#circleLeft)" stroke="none" />
                </svg>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-3 font-medium">Matches in both tables</p>
              </div>

              {/* LEFT JOIN */}
              <div className="bg-white dark:bg-[#162032] p-6 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col items-center hover:border-green-500/30 transition-colors shadow-sm dark:shadow-none">
                <h4 className="font-bold text-gray-900 dark:text-white mb-4 text-xs tracking-widest">LEFT JOIN</h4>
                <svg width="120" height="80" viewBox="0 0 120 80" className="opacity-90">
                  <circle cx="45" cy="40" r="30" fill="#22c55e" fillOpacity="0.4" stroke="#22c55e" strokeWidth="2" />
                  <circle cx="75" cy="40" r="30" fill="none" stroke="#64748b" strokeWidth="2" />
                </svg>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-3 font-medium">All from Left, matches from Right</p>
              </div>

              {/* RIGHT JOIN */}
              <div className="bg-white dark:bg-[#162032] p-6 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col items-center hover:border-green-500/30 transition-colors shadow-sm dark:shadow-none">
                <h4 className="font-bold text-gray-900 dark:text-white mb-4 text-xs tracking-widest">RIGHT JOIN</h4>
                <svg width="120" height="80" viewBox="0 0 120 80" className="opacity-90">
                  <circle cx="45" cy="40" r="30" fill="none" stroke="#64748b" strokeWidth="2" />
                  <circle cx="75" cy="40" r="30" fill="#22c55e" fillOpacity="0.4" stroke="#22c55e" strokeWidth="2" />
                </svg>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-3 font-medium">All from Right, matches from Left</p>
              </div>

              {/* FULL OUTER JOIN */}
              <div className="bg-white dark:bg-[#162032] p-6 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col items-center hover:border-green-500/30 transition-colors shadow-sm dark:shadow-none">
                <h4 className="font-bold text-gray-900 dark:text-white mb-4 text-xs tracking-widest">FULL OUTER</h4>
                <svg width="120" height="80" viewBox="0 0 120 80" className="opacity-90">
                  <circle cx="45" cy="40" r="30" fill="#22c55e" fillOpacity="0.4" stroke="#22c55e" strokeWidth="2" />
                  <circle cx="75" cy="40" r="30" fill="#22c55e" fillOpacity="0.4" stroke="#22c55e" strokeWidth="2" />
                </svg>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-3 font-medium">Everything from both tables</p>
              </div>
            </div>

            <CodeBlock
              label="Inner Join"
              id="inner-join"
              code={`-- Combine users and orders where IDs match\nSELECT users.name, orders.amount\nFROM users\nINNER JOIN orders ON users.id = orders.user_id;`}
              example={{
                input: [
                  { name: 'users', data: MOCK_USERS },
                  { name: 'orders', data: MOCK_ORDERS }
                ],
                output: [
                  { name: 'Alice', amount: 50 },
                  { name: 'Bob', amount: 20 },
                  { name: 'Alice', amount: 100 }
                ]
              }}
            />

            <CodeBlock
              label="Left Join"
              id="left-join"
              code={`-- Keep all users, even if they have no orders\nSELECT users.name, orders.amount\nFROM users\nLEFT JOIN orders ON users.id = orders.user_id;`}
              example={{
                input: [
                  { name: 'users', data: MOCK_USERS },
                  { name: 'orders', data: MOCK_ORDERS }
                ],
                output: [
                  { name: 'Alice', amount: 50 },
                  { name: 'Alice', amount: 100 },
                  { name: 'Bob', amount: 20 },
                  { name: 'Charlie', amount: null }
                ]
              }}
            />

            <CodeBlock
              label="Right Join"
              id="right-join"
              code={`-- Keep all orders, even if they have no matching user (rare)\nSELECT users.name, orders.amount\nFROM users\nRIGHT JOIN orders ON users.id = orders.user_id;`}
              example={{
                input: [
                  { name: 'users', data: MOCK_USERS },
                  { name: 'orders', data: MOCK_ORDERS }
                ],
                output: [
                  { name: 'Alice', amount: 50 },
                  { name: 'Alice', amount: 100 },
                  { name: 'Bob', amount: 20 }
                ]
              }}
            />

            <CodeBlock
              label="Full Outer Join"
              id="full-outer-join"
              code={`-- Keep everything from both tables\nSELECT users.name, orders.amount\nFROM users\nFULL OUTER JOIN orders ON users.id = orders.user_id;`}
              example={{
                input: [
                  { name: 'users', data: MOCK_USERS },
                  { name: 'orders', data: MOCK_ORDERS }
                ],
                output: [
                  { name: 'Alice', amount: 50 },
                  { name: 'Alice', amount: 100 },
                  { name: 'Bob', amount: 20 },
                  { name: 'Charlie', amount: null }
                ]
              }}
            />
          </section>

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* SET OPERATIONS */}
          <section id="sets" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-500/20 rounded-lg text-indigo-600 dark:text-indigo-400">
                <FiLayers size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Set Operations</h2>
            </div>
            <div className="space-y-8">
              <CodeBlock
                label="Union All"
                id="union-all"
                code={`-- Combine two result sets (keeps duplicates)\nSELECT name FROM users\nUNION ALL\nSELECT name FROM employees;`}
                example={{
                  input: [
                    { name: 'users', data: MOCK_USERS },
                    { name: 'employees', data: MOCK_EMPLOYEES }
                  ],
                  output: [
                    { name: 'Alice' }, { name: 'Bob' }, { name: 'Charlie' },
                    { name: 'Alice' }, { name: 'Bob' }, { name: 'Charlie' }
                  ]
                }}
              />
              <CodeBlock
                label="Union"
                id="union-distinct"
                code={`-- Combine two result sets (removes duplicates)\nSELECT name FROM users\nUNION\nSELECT name FROM employees;`}
                example={{
                  input: [
                    { name: 'users', data: MOCK_USERS },
                    { name: 'employees', data: MOCK_EMPLOYEES }
                  ],
                  output: [
                    { name: 'Alice' }, { name: 'Bob' }, { name: 'Charlie' }
                  ]
                }}
              />
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* AGGREGATION */}
          <section id="aggregation" className="scroll-mt-32">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Aggregation & Grouping</h2>
            <div className="space-y-8">
              <CodeBlock
                label="Basic Aggregates"
                id="agg-basic"
                code={`-- Count, Sum, Avg, Min, Max\nSELECT \n  COUNT(*) as total_rows,\n  SUM(amount) as total_revenue,\n  AVG(amount) as avg_amount,\n  MIN(amount) as min_val,\n  MAX(amount) as max_val\nFROM orders;`}
                example={{
                  input: [{ name: 'orders', data: MOCK_ORDERS }],
                  output: [{ total_rows: 3, total_revenue: 170, avg_amount: 56.6, min_val: 20, max_val: 100 }]
                }}
              />
              <CodeBlock
                label="Count Distinct"
                id="count-distinct"
                code={`-- Count unique foreign keys\nSELECT COUNT(DISTINCT user_id) as active_users \nFROM orders;`}
                example={{
                  input: [{ name: 'orders', data: MOCK_ORDERS }],
                  output: [{ active_users: 2 }]
                }}
              />
              <CodeBlock
                label="Group By & Having"
                id="groupby"
                code={`-- Grouping with filter conditions\nSELECT country, COUNT(*) as count\nFROM users\nGROUP BY country\nHAVING COUNT(*) > 1;`}
                example={{
                  input: [{ name: 'users', data: MOCK_USERS }],
                  output: [
                    { country: 'USA', count: 2 }
                  ]
                }}
              />
            </div>
          </section>

          {/* WINDOW FUNCTIONS */}
          <section id="window" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-orange-100 dark:bg-orange-500/20 rounded-lg text-orange-600 dark:text-orange-400">
                <FiLayers size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Window Functions</h2>
            </div>

            <CodeBlock
              label="Row Number"
              id="ranking"
              code={`-- Assign a rank based on name order\nSELECT name, ROW_NUMBER() OVER(ORDER BY name) as rank \nFROM users;`}
              example={{
                input: [{ name: 'users', data: MOCK_USERS }],
                output: [
                  { name: 'Alice', rank: 1 },
                  { name: 'Bob', rank: 2 },
                  { name: 'Charlie', rank: 3 }
                ]
              }}
            />
            <CodeBlock
              label="Running Total"
              id="partition"
              code={`-- Calculate cumulative sales per region\nSELECT date, region, amount,\n  SUM(amount) OVER(PARTITION BY region ORDER BY date) as running_total\nFROM sales;`}
              example={{
                input: [{ name: 'sales', data: MOCK_SALES }],
                output: [
                  { date: '2023-01', region: 'North', amount: 100, running_total: 100 },
                  { date: '2023-02', region: 'North', amount: 150, running_total: 250 },
                  { date: '2023-01', region: 'South', amount: 200, running_total: 200 }
                ]
              }}
            />
            <CodeBlock
              label="Rank"
              id="rank"
              code={`-- Rank with gaps for ties (1, 1, 3)\nSELECT name, RANK() OVER(ORDER BY name) as rank \nFROM users;`}
              example={{
                input: [{ name: 'users', data: MOCK_USERS }],
                output: [
                  { name: 'Alice', rank: 1 },
                  { name: 'Bob', rank: 2 },
                  { name: 'Charlie', rank: 3 }
                ]
              }}
            />
            <CodeBlock
              label="Lag"
              id="lag"
              code={`-- Access previous row's value\nSELECT date, amount, LAG(amount) OVER(ORDER BY date) as prev_amount \nFROM sales \nWHERE region = 'North';`}
              example={{
                input: [{ name: 'sales', data: MOCK_SALES }],
                output: [
                  { date: '2023-01', amount: 100, prev_amount: null },
                  { date: '2023-02', amount: 150, prev_amount: 100 }
                ]
              }}
            />
          </section>

          {/* SUBQUERIES & CTEs */}
          <section id="ctes" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-pink-100 dark:bg-pink-500/20 rounded-lg text-pink-600 dark:text-pink-400">
                <FiLayers size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Subqueries & CTEs</h2>
            </div>
            <div className="space-y-8">
              <CodeBlock
                label="Common Table Expression (CTE)"
                id="cte"
                code={`-- Define a temporary result set\nWITH high_sales AS (\n  SELECT * FROM orders WHERE amount > 50\n)\nSELECT * FROM high_sales;`}
                example={null}
              />
              <CodeBlock
                label="Basic Subquery"
                id="subquery"
                code={`-- Filter based on another query result\nSELECT * FROM orders \nWHERE amount > (SELECT AVG(amount) FROM orders);`}
                example={null}
              />
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-800" />

          {/* DATE & TIME  */}
          <section id="dates" className="scroll-mt-32">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-teal-100 dark:bg-teal-500/20 rounded-lg text-teal-600 dark:text-teal-400">
                <FiClock size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Date & Time Functions</h2>
            </div>
            <CodeBlock
              label="Current Date"
              id="current-date"
              code={`-- Get today's date\nSELECT CURRENT_DATE as today;`}
              example={{
                input: [],
                output: [{ today: '2023-11-14' }] // Mock date
              }}
            />
            <CodeBlock
              label="Date Add"
              id="date-add"
              code={`-- Add 7 days to a date\nSELECT date, DATE_ADD(CAST(date AS DATE), INTERVAL 7 DAY) as next_week \nFROM orders;`}
              example={{
                input: [{ name: 'orders', data: MOCK_ORDERS }],
                output: MOCK_ORDERS.map(o => ({ date: o.date, next_week: '...' })) // Simplified for display
              }}
            />
            <CodeBlock
              label="Date Diff"
              id="datediff"
              code={`-- Count days between two dates\nSELECT end_date, start_date, DATE_DIFF('day', start_date, end_date) as days_diff\nFROM projects;`}
              example={null}
            />
            <CodeBlock
              label="Date Truncate"
              id="date-trunc"
              code={`-- Truncate date to start of month\nSELECT date, DATE_TRUNC('month', CAST(date AS DATE)) as month_start \nFROM orders;`}
              example={{
                input: [{ name: 'orders', data: MOCK_ORDERS }],
                output: [{ date: '2023-01-05', month_start: '2023-01-01' }]
              }}
            />
            <CodeBlock
              label="Date Extract"
              id="extract"
              code={`-- Extract specific part of date\nSELECT date, EXTRACT(YEAR FROM CAST(date AS DATE)) as year \nFROM orders;`}
              example={{
                input: [{ name: 'orders', data: MOCK_ORDERS }],
                output: [{ date: '2023-01-05', year: 2023 }]
              }}
            />
          </section>



          {/* TYPE CONVERSION */}
          <section id="conversion" className="scroll-mt-32">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Type Conversion</h2>
            <div className="space-y-8">
              <CodeBlock
                label="Cast"
                id="cast"
                code={`-- Convert value to different type\nSELECT CAST('100' AS INTEGER) as number_value;`}
                example={{
                  input: [],
                  output: [{ number_value: 100 }]
                }}
              />
              <CodeBlock
                label="Coalesce"
                id="coalesce"
                code={`-- Handle NULL values with a fallback\nSELECT item, COALESCE(stock, 0) as available_stock \nFROM inventory;`}
                example={{
                  input: [{ name: 'inventory', data: MOCK_INVENTORY }],
                  output: [
                    { item: 'Widget A', available_stock: 50 },
                    { item: 'Widget B', available_stock: 0 },
                    { item: 'Widget C', available_stock: 0 }
                  ]
                }}
              />
            </div>
          </section>





        </main>
      </div>
    </div>
  );
};

export default SQLCheatsheet;
